/**
 * F-110 notification bell: unread badge in the top bar, dropdown inbox under
 * it. Mounted on BOTH the dashboard and the board screen — a mention on one
 * board has to be visible while you are looking at another.
 *
 * Clicking a row navigates to `/b/<board>?thread=<id>`, which BoardScreen
 * consumes by opening that thread and panning its pin into view (F-123-style
 * deep link). That is the whole point of the feature: the notification is a
 * pointer AT the comment, not a copy of it.
 */
import { NOTIFICATION_KIND_LABELS, type NotificationItem } from "@kola/shared";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "zustand";

import { colorForName, initialsFor } from "../../core/identity.js";
import { RowButton } from "../../ui/RowButton.js";
import { formatRelativeTime } from "../comments/anchoring.js";
import { notificationsStore, startNotificationPolling } from "./store.js";

const iconBell = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M8 2a4 4 0 0 0-4 4c0 3-1.2 4-1.2 4h10.4S12 9 12 6a4 4 0 0 0-4-4Z" />
    <path d="M9.4 12.5a1.6 1.6 0 0 1-2.8 0" />
  </svg>
);

function NotificationRow({
  item,
  now,
  onOpen,
}: {
  item: NotificationItem;
  now: number;
  onOpen(item: NotificationItem): void;
}) {
  return (
    <li>
      <RowButton
        className={`notif-item${item.readAt === null ? " unread" : ""}`}
        onActivate={() => onOpen(item)}
      >
        <span className="cm-avatar" style={{ background: colorForName(item.actorName) }}>
          {initialsFor(item.actorName)}
        </span>
        <span className="notif-item-main">
          <span className="notif-item-head">
            <span className="notif-actor">{item.actorName}</span>
            <span className="notif-kind">{NOTIFICATION_KIND_LABELS[item.kind]}</span>
            <span className="cm-time">{formatRelativeTime(item.ts, now)}</span>
          </span>
          <span className="notif-excerpt">{item.excerpt}</span>
          <span className="notif-board">{item.boardName}</span>
        </span>
        {item.readAt === null && <span className="notif-dot" aria-label="Unread" />}
      </RowButton>
    </li>
  );
}

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const items = useStore(notificationsStore, (s) => s.items);
  const unread = useStore(notificationsStore, (s) => s.unread);
  const loading = useStore(notificationsStore, (s) => s.loading);
  const error = useStore(notificationsStore, (s) => s.error);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => startNotificationPolling(), []);

  // Outside click / Esc close, matching the board top bar's menu.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (wrapRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const openInbox = () => {
    setOpen((wasOpen) => !wasOpen);
    // Opening refreshes, so a bell clicked 29 s into the poll interval is not
    // showing an inbox that is nearly a poll out of date.
    if (!open) void notificationsStore.getState().refresh();
  };

  const openItem = (item: NotificationItem) => {
    setOpen(false);
    if (item.readAt === null) void notificationsStore.getState().markRead([item.id]);
    navigate(`/b/${encodeURIComponent(item.boardId)}?thread=${encodeURIComponent(item.threadId)}`);
  };

  const now = Date.now();

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`icon-btn${open ? " active" : ""}`}
        onClick={openInbox}
        aria-expanded={open}
        aria-label={unread === 0 ? "Notifications" : `Notifications (${unread} unread)`}
        data-tip="Notifications — mentions, replies, comments on your elements"
      >
        {iconBell}
        {unread > 0 && <span className="notif-badge">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="notif-panel" role="dialog" aria-label="Notifications">
          <div className="notif-head">
            <span className="notif-title">Notifications</span>
            <span className="spacer" />
            {unread > 0 && (
              <button
                type="button"
                className="cm-link-btn"
                onClick={() => void notificationsStore.getState().markRead()}
              >
                Mark all read
              </button>
            )}
          </div>
          {error && (
            <div className="notif-note">Couldn&rsquo;t refresh — showing the last update.</div>
          )}
          {loading && items.length === 0 ? (
            <div className="notif-empty">Loading…</div>
          ) : items.length === 0 ? (
            <div className="notif-empty">
              Nothing yet. You&rsquo;ll hear about @mentions, replies in your threads, and comments
              on your elements.
            </div>
          ) : (
            <ul className="notif-list">
              {items.map((item) => (
                <NotificationRow key={item.id} item={item} now={now} onOpen={openItem} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
